/**
 * Layout — operations-console shell for the water tenant.
 *
 *   ┌─────────────────────────────────────────────────────────┐
 *   │ Header   Brand · KPI strip · profile                    │
 *   ├──────────────┬─────────────────────────┬──────────────┤
 *   │  AlertsRail  │  Map (always on top of  │  Workspace   │
 *   │  (left,      │  full vertical area;    │  tabs:       │
 *   │   280px)     │  SCADA markers + chat   │  · Chat      │
 *   │              │  results overlaid)      │  · Dossier   │
 *   │              │                         │  · Analytics │
 *   │              │                         │  (right,     │
 *   │              │                         │   400px)     │
 *   └──────────────┴─────────────────────────┴──────────────┘
 *
 * No bottom panel. Each column owns its own vertical space and scroll.
 */
import { useMemo, useState, useEffect } from 'react';
import '../styles/Layout.css';
import RoleSwitcher from './RoleSwitcher';
import ConnectedSourcesPanel from './ConnectedSourcesPanel';

const RAIL_COLLAPSED_KEY = 'nexus.alertsRail.collapsed';

export default function Layout({
  brand,
  subtitle,
  alerts = [],          // full list of active alerts (for header KPI + rail)
  leftRail,             // <AlertsRail .../>
  map,                  // <Map .../>
  workspace,            // <WorkspaceTabs .../>
}) {
  const kpis = useMemo(() => {
    const total = alerts.length;
    const critical = alerts.filter(a => a.severity === 'critical').length;
    const warning = alerts.filter(a => a.severity === 'warning').length;
    const impact = alerts.reduce((s, a) => s + (a.impact_value || a.impact_bbl_day || 0), 0);
    const unit = (alerts[0] && alerts[0].impact_unit) || 'bbl/day';
    return { total, critical, warning, impact, unit };
  }, [alerts]);

  // Collapsible left rail (PM feedback #1) — persisted to localStorage.
  const [railCollapsed, setRailCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(RAIL_COLLAPSED_KEY) === '1';
  });
  const toggleRail = () => {
    setRailCollapsed(prev => {
      const next = !prev;
      try { window.localStorage.setItem(RAIL_COLLAPSED_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  };
  // After the CSS transition settles, force Leaflet to recompute its tile grid.
  useEffect(() => {
    const t = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 280);
    return () => clearTimeout(t);
  }, [railCollapsed]);

  return (
    <div className={`ops-shell ${railCollapsed ? 'rail-collapsed' : ''}`}>
      <header className="ops-header">
        <div className="ops-brand">
          <div className="ops-brand-name">{brand}</div>
          <div className="ops-brand-sub">{subtitle}</div>
        </div>

        <div className="ops-kpis" aria-label="Network status">
          <KpiBadge
            label="Active alerts"
            value={kpis.total.toLocaleString()}
            tone={kpis.critical > 0 ? 'alert' : 'neutral'}
            sub={`${kpis.critical} critical`}
          />
          <KpiBadge
            label="Capacity at risk"
            value={`${Math.round(kpis.impact / 1000).toLocaleString()}K`}
            tone="alert"
            sub={kpis.unit}
          />
          <KpiBadge
            label="Network"
            value="LIVE"
            tone="ok"
            sub="all systems"
          />
        </div>

        <div style={{ marginLeft: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <RoleSwitcher />
        </div>
      </header>

      <main className="ops-main">
        <aside className="ops-left">
          {railCollapsed ? (
            <CollapsedRail kpis={kpis} onExpand={toggleRail} />
          ) : (
            <>
              <div className="ops-rail-toggle-wrap">
                <button className="ops-rail-toggle" onClick={toggleRail} aria-label="Collapse alerts rail" title="Collapse">‹</button>
              </div>
              <div style={{ flex: '1 1 auto', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: '1 1 60%', overflow: 'hidden', minHeight: 0 }}>
                  {leftRail}
                </div>
                <div style={{ flex: '0 0 auto', borderTop: '1px solid rgba(148,163,184,0.18)', maxHeight: '40%', overflowY: 'auto' }}>
                  <ConnectedSourcesPanel />
                </div>
              </div>
            </>
          )}
        </aside>
        <section className="ops-center">{map}</section>
        <aside className="ops-right">{workspace}</aside>
      </main>
    </div>
  );
}

function CollapsedRail({ kpis, onExpand }) {
  return (
    <div className="ops-rail-collapsed">
      <button className="ops-rail-toggle ops-rail-toggle-collapsed" onClick={onExpand} aria-label="Expand alerts rail" title="Expand">›</button>
      <div className="ops-rail-mini">
        <div className="ops-rail-mini-icon" title={`${kpis.critical} critical`}>
          <span className="dot dot-crit" />
          <span className="ops-rail-mini-num">{kpis.critical}</span>
        </div>
        <div className="ops-rail-mini-icon" title={`${kpis.warning} warning`}>
          <span className="dot dot-warn" />
          <span className="ops-rail-mini-num">{kpis.warning}</span>
        </div>
        <div className="ops-rail-mini-icon" title={`${kpis.total} total`}>
          <span className="dot dot-total" />
          <span className="ops-rail-mini-num">{kpis.total}</span>
        </div>
      </div>
    </div>
  );
}

function KpiBadge({ label, value, sub, tone = 'neutral' }) {
  return (
    <div className={`ops-kpi ops-kpi-${tone}`}>
      <div className="ops-kpi-label">{label}</div>
      <div className="ops-kpi-value">{value}</div>
      <div className="ops-kpi-sub">{sub}</div>
    </div>
  );
}
